import { Injectable, inject, signal, computed } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, AccountInfo } from '@azure/msal-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

export type UserRole = 'admin' | 'user';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  groups: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly destroy$ = new Subject<void>();

  // Signals for reactive state
  private readonly _isAuthenticated = signal(false);
  private readonly _userProfile = signal<UserProfile | null>(null);
  private readonly _isLoading = signal(true);

  // Public computed signals
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly userProfile = this._userProfile.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();

  // Role-based computed signals
  readonly isAdmin = computed(() =>
    this._userProfile()?.roles.includes('admin') ?? false
  );

  readonly isUser = computed(() =>
    this._userProfile()?.roles.includes('user') ?? false
  );

  // Check if user has any administrative role
  readonly hasAdminAccess = computed(() => this.isAdmin());

  constructor() {
    this.initializeAuth();
  }

  private initializeAuth(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this._isLoading.set(false);
      });
  }

  private checkAndSetActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const activeAccount = this.msalService.instance.getActiveAccount() ?? accounts[0];
      this.msalService.instance.setActiveAccount(activeAccount);
      this._isAuthenticated.set(true);
      this.setUserProfile(activeAccount);
    } else {
      this._isAuthenticated.set(false);
      this._userProfile.set(null);
    }
  }

  private setUserProfile(account: AccountInfo): void {
    const idTokenClaims = account.idTokenClaims as Record<string, unknown>;
    const groups = (idTokenClaims?.['groups'] as string[]) ?? [];
    const roles = this.mapGroupsToRoles(groups);

    // Debug logging
    console.log('[AuthService] Token claims:', idTokenClaims);
    console.log('[AuthService] Groups from token:', groups);
    console.log('[AuthService] Mapped roles:', roles);
    console.log('[AuthService] Expected group IDs:', environment.securityGroups);

    const profile: UserProfile = {
      id: account.localAccountId,
      email: account.username,
      name: account.name ?? account.username,
      roles,
      groups
    };

    this._userProfile.set(profile);
  }

  private mapGroupsToRoles(groups: string[]): UserRole[] {
    const roles: UserRole[] = [];
    const { securityGroups } = environment;

    if (groups.includes(securityGroups.admin)) {
      roles.push('admin');
    }

    // If no specific role found, assign user role
    if (roles.length === 0) {
      roles.push('user');
    }

    return roles;
  }

  login(): void {
    this.msalService.loginRedirect({
      scopes: environment.apiScopes
    });
  }

  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.msalConfig.auth.postLogoutRedirectUri
    });
  }

  hasRole(role: UserRole): boolean {
    return this._userProfile()?.roles.includes(role) ?? false;
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const userRoles = this._userProfile()?.roles ?? [];
    return roles.some(role => userRoles.includes(role));
  }

  getTenantId(): string | null {
    // TODO: Implement proper tenant ID retrieval
    // For now, return a default tenant ID
    return 'demo-tenant';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
